import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  isExchangeablePortalAuthPath,
  PORTAL_QUERY_PARAMS,
  portalAuthPaths,
  portalHomePath,
  SIGNUP_SCREEN,
} from "./portal_paths";

// A fragment still carrying credentials means the exchange is mid-flight, and
// replacing the URL would discard them before anything reads them.
const carriesCredentials = (hash: string) =>
  /(?:access|id)_token|(?:^|[#&])code=/.test(hash);

// The identity provider will not validate a partner slug inside a path, so signing
// out returns to a fixed, slug-free address. Moving the visitor on to the partner's
// own address makes that a momentary transit rather than somewhere they are left
// standing, so the partner-named address is the one they see and can share.
export function useCanonicalPortalUrl() {
  const history = useHistory();
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    const onReturnAddress = pathname === portalAuthPaths.callback;

    if (!isExchangeablePortalAuthPath(pathname) && !onReturnAddress) {
      return;
    }

    // The return address is only safe to leave once no credentials are in flight.
    // A visitor stranded there after a failed exchange should not be left reading a
    // URL that was never meant to be seen, so an empty or error-bearing fragment
    // is exchanged like any other auth path.
    if (onReturnAddress && carriesCredentials(hash)) {
      return;
    }

    // Only a partner named in the URL may be moved into the path. A slug resolved
    // from storage belongs to whoever used this browser last, and promoting it
    // would publish another tenant's address to someone who never asked for it.
    const params = new URLSearchParams(search);
    const partnerSlug = params.get(PORTAL_QUERY_PARAMS.partnerSlug);

    if (!partnerSlug) {
      return;
    }

    // Which form to show is stated by the path as well as by the parameter, and the
    // path is about to be replaced — so make the parameter carry it.
    if (pathname === portalAuthPaths.signup) {
      params.set(PORTAL_QUERY_PARAMS.screen, SIGNUP_SCREEN);
    }

    // Everything else the address carried is forwarded untouched — a checkout
    // hand-off's email, campaign parameters. Only the partner is dropped, because
    // the path states it.
    params.delete(PORTAL_QUERY_PARAMS.partnerSlug);
    const remaining = params.toString();

    history.replace(
      `${portalHomePath(partnerSlug)}${remaining ? `?${remaining}` : ""}`
    );
  }, [pathname, search, hash, history]);
}
